import React, { useState } from 'react';
import { FaArrowLeft, FaShoppingCart, FaHeart, FaShare, FaExpand } from 'react-icons/fa';
import translationService from '../../services/translationService';
import styles from './EnhancedProductDetail.module.css';

// Size guide data
const SIZE_GUIDE = {
  XS: { cm: { chest: '76-81', waist: '58-63', hips: '81-86' }, inch: { chest: '30-32', waist: '23-25', hips: '32-34' } },
  S: { cm: { chest: '81-86', waist: '63-68', hips: '86-91' }, inch: { chest: '32-34', waist: '25-27', hips: '34-36' } },
  M: { cm: { chest: '86-91', waist: '68-73', hips: '91-96' }, inch: { chest: '34-36', waist: '27-29', hips: '36-38' } },
  L: { cm: { chest: '91-96', waist: '73-78', hips: '96-101' }, inch: { chest: '36-38', waist: '29-31', hips: '38-40' } },
  XL: { cm: { chest: '96-101', waist: '78-83', hips: '101-106' }, inch: { chest: '38-40', waist: '31-33', hips: '40-42' } },
  XXL: { cm: { chest: '101-106', waist: '83-88', hips: '106-111' }, inch: { chest: '40-42', waist: '33-35', hips: '42-44' } }
};

export default function EnhancedProductDetail({ product, onAddToCart, onBack, lang = 'en' }) {
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [addMessage, setAddMessage] = useState('');

  // Get translated content based on language
  const getTranslatedContent = (field) => {
    return translationService.getTranslatedContent(product, field, lang);
  };

  // UI text translations
  const uiText = {
    en: {
      backToShop: 'Back to Shop',
      selectSize: 'Select Size',
      sizeGuide: 'Size Guide',
      selected: 'Selected',
      selectColor: 'Select Color',
      quantity: 'Quantity',
      addToCart: 'Add to Cart',
      amanNote: 'This is an Aman product. Please visit our physical store to purchase.',
      freeShipping: 'Free Shipping',
      easyReturns: 'Easy Returns',
      securePayment: 'Secure Payment',
      note: 'Note',
      sizeGuideNote: 'Measurements are approximate and may vary by brand. We recommend trying on items when possible.',
      size: 'Size',
      chest: 'Chest (cm/inch)',
      waist: 'Waist (cm/inch)',
      hips: 'Hips (cm/inch)'
    },
    ar: {
      backToShop: 'العودة للمتجر',
      selectSize: 'اختر المقاس',
      sizeGuide: 'دليل المقاسات',
      selected: 'المختار',
      selectColor: 'اختر اللون',
      quantity: 'الكمية',
      addToCart: 'أضف للسلة',
      amanNote: 'هذا منتج أمان. يرجى زيارة متجرنا الفعلي للشراء.',
      freeShipping: 'شحن مجاني',
      easyReturns: 'إرجاع سهل',
      securePayment: 'دفع آمن',
      note: 'ملاحظة',
      sizeGuideNote: 'القياسات تقريبية وقد تختلف حسب العلامة التجارية. نوصي بتجربة المنتجات عند الإمكان.',
      size: 'المقاس',
      chest: 'الصدر (سم/بوصة)',
      waist: 'الخصر (سم/بوصة)',
      hips: 'الورك (سم/بوصة)'
    },
    tr: {
      backToShop: 'Mağazaya Dön',
      selectSize: 'Beden Seç',
      sizeGuide: 'Beden Rehberi',
      selected: 'Seçilen',
      selectColor: 'Renk Seç',
      quantity: 'Miktar',
      addToCart: 'Sepete Ekle',
      amanNote: 'Bu bir Aman ürünüdür. Satın almak için lütfen fiziksel mağazamızı ziyaret edin.',
      freeShipping: 'Ücretsiz Kargo',
      easyReturns: 'Kolay İade',
      securePayment: 'Güvenli Ödeme',
      note: 'Not',
      sizeGuideNote: 'Ölçüler yaklaşıktır ve markaya göre değişebilir. Mümkün olduğunda ürünleri denemenizi öneririz.',
      size: 'Beden',
      chest: 'Göğüs (cm/inch)',
      waist: 'Bel (cm/inch)',
      hips: 'Kalça (cm/inch)'
    }
  }[lang];

  // Combine main image with additional images
  const allImages = [product.mainImage, ...(product.additionalImages || [])].filter(Boolean);

  const handleAddToCart = () => {
    if (!selectedSize) {
      alert('Please select a size');
      return;
    }
    if (isAdding) return; // Prevent double click
    setIsAdding(true);
    // Show message to user
    setAddMessage(lang === 'ar' ? 'تمت الإضافة إلى السلة بنجاح' : lang === 'tr' ? 'Sepete eklendi!' : 'Added to cart successfully!');
    const productWithSize = {
      ...product,
      selectedSize,
      selectedColor,
      quantity
    };
    onAddToCart(productWithSize);
    setTimeout(() => {
      setIsAdding(false);
    }, 3000); // Block for 3 seconds
    setTimeout(() => {
      setAddMessage('');
    }, 2000); // Hide message after 2 seconds
  };

  const handleImageClick = (index) => {
    setSelectedImage(index);
  };

  const formatPrice = (price) => {
    return '$' + Number(price).toLocaleString('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const calculateDiscount = () => {
    if (!product.isOffer) return 0;
    return Math.round(((product.price - product.offerPrice) / product.price) * 100);
  };

  return (
    <div className={styles.container}>
      {/* Back Button */}
      <button onClick={onBack} className={styles.backButton}>
        <FaArrowLeft /> {uiText.backToShop}
      </button>

      <div className={styles.productContent}>
        {/* Image Gallery */}
        <div className={styles.imageSection}>
          <div className={styles.mainImageContainer}>
            <img 
              src={allImages[selectedImage]} 
              alt={`${getTranslatedContent('name') || product.name} - Image ${selectedImage + 1}`}
              className={styles.mainImage}
              onClick={() => setShowImageModal(true)}
            />
            <button 
              className={styles.expandButton}
              onClick={() => setShowImageModal(true)}
            >
              <FaExpand />
            </button>
          </div>

          {/* Thumbnail Gallery */}
          {allImages.length > 1 && (
            <div className={styles.thumbnailGallery}>
              {allImages.map((image, index) => (
                <div
                  key={index}
                  className={`${styles.thumbnail} ${selectedImage === index ? styles.active : ''}`}
                  onClick={() => handleImageClick(index)}
                >
                  <img src={image} alt={`${getTranslatedContent('name') || product.name} - Thumbnail ${index + 1}`} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Product Information */}
        <div className={styles.productInfo}>
          <div className={styles.productHeader}>
            <h1 className={styles.productTitle}>{getTranslatedContent('name') || product.name}</h1>
            <div className={styles.productActions}>
              <button 
                className={`${styles.actionBtn} ${isWishlisted ? styles.wishlisted : ''}`}
                onClick={() => setIsWishlisted(!isWishlisted)}
              >
                <FaHeart />
              </button>
              <button className={styles.actionBtn}>
                <FaShare />
              </button>
            </div>
          </div>

          <p className={styles.description}>{getTranslatedContent('description') || product.description}</p>

          {/* Price Section */}
          <div className={styles.priceSection}>
            {product.isOffer ? (
              <div className={styles.offerPricing}>
                <span className={styles.originalPrice}>{formatPrice(product.price)}</span>
                <span className={styles.offerPrice}>{formatPrice(product.offerPrice)}</span>
                <span className={styles.discountBadge}>{calculateDiscount()}% OFF</span>
              </div>
            ) : (
              <span className={styles.price}>{formatPrice(product.price)}</span>
            )}
          </div>

          {/* Size Selection */}
          <div className={styles.sizeSection}>
            <div className={styles.sizeHeader}>
              <h3>{uiText.selectSize}</h3>
              <button
                type="button"
                className={styles.sizeGuideBtn}
                onClick={() => setShowSizeGuide(true)}
              >
                {uiText.sizeGuide}
              </button>
            </div>

            <div className={styles.sizeGrid}>
              {product.sizes?.map(size => (
                <button
                  key={size}
                  className={`${styles.sizeBtn} ${selectedSize === size ? styles.selected : ''}`}
                  onClick={() => setSelectedSize(size)}
                >
                  {size}
                </button>
              ))}
            </div>

            {selectedSize && (
              <p className={styles.selectedSizeText}>
                {uiText.selected}: <strong>{selectedSize}</strong>
              </p>
            )}
          </div>

          {/* Color Selection */}
          {product.colors && product.colors.length > 0 && (
            <div className={styles.colorSection}>
              <h3>{uiText.selectColor}</h3>
              <div className={styles.colorGrid}>
                {product.colors.map(color => (
                  <button
                    key={color}
                    className={`${styles.colorBtn} ${selectedColor === color ? styles.selected : ''}`}
                    onClick={() => setSelectedColor(color)}
                  >
                    {color}
                  </button>
                ))}
              </div>

              {selectedColor && (
                <p className={styles.selectedColorText}>
                  {uiText.selected}: <strong>{selectedColor}</strong>
                </p>
              )}
            </div>
          )}

          {/* Quantity */}
          <div className={styles.quantitySection}>
            <label>{uiText.quantity}:</label>
            <div className={styles.quantityControls}>
              <button 
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={quantity <= 1}
              >
                -
              </button>
              <span>{quantity}</span>
              <button onClick={() => setQuantity(quantity + 1)}>
                +
              </button>
            </div>
          </div>

          {/* Add to Cart Button */}
          {!product.isAman ? (
            <>
              <button 
                onClick={handleAddToCart}
                className={styles.addToCartBtn}
                disabled={!selectedSize || isAdding}
              >
                <FaShoppingCart /> {isAdding ? (lang === 'ar' ? '...جاري الإضافة' : lang === 'tr' ? 'Ekleniyor...' : 'Adding...') : uiText.addToCart}
              </button>
              {addMessage && (
                <div style={{ marginTop: '1rem', color: 'green', fontWeight: 600, fontSize: '1.1rem' }}>{addMessage}</div>
              )}
            </>
          ) : (
            <div className={styles.amanNote}>
              <p>{uiText.amanNote}</p>
            </div>
          )}

          {/* Product Features */}
          <div className={styles.productFeatures}>
            <div className={styles.feature}>
              <span className={styles.featureIcon}>🚚</span>
              <span>{uiText.freeShipping}</span>
            </div>
            <div className={styles.feature}>
              <span className={styles.featureIcon}>🔄</span>
              <span>{uiText.easyReturns}</span>
            </div>
            <div className={styles.feature}>
              <span className={styles.featureIcon}>🛡️</span>
              <span>{uiText.securePayment}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Size Guide Modal */}
      {showSizeGuide && (
        <div className={styles.modalOverlay} onClick={() => setShowSizeGuide(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{uiText.sizeGuide}</h3>
              <button 
                className={styles.closeBtn}
                onClick={() => setShowSizeGuide(false)}
              >
                ×
              </button>
            </div>
            
            <div className={styles.sizeGuideContent}>
              <div className={styles.sizeGuideTable}>
                <table>
                  <thead>
                    <tr>
                      <th>{uiText.size}</th>
                      <th>{uiText.chest}</th>
                      <th>{uiText.waist}</th>
                      <th>{uiText.hips}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(SIZE_GUIDE).map(([size, measurements]) => (
                      <tr key={size}>
                        <td className={styles.sizeLabel}>{size}</td>
                        <td>{measurements.cm.chest} / {measurements.inch.chest}</td>
                        <td>{measurements.cm.waist} / {measurements.inch.waist}</td>
                        <td>{measurements.cm.hips} / {measurements.inch.hips}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className={styles.sizeGuideNote}>
                <p><strong>{uiText.note}:</strong> {uiText.sizeGuideNote}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div className={styles.imageModalOverlay} onClick={() => setShowImageModal(false)}>
          <div className={styles.imageModal} onClick={(e) => e.stopPropagation()}>
            <button 
              className={styles.closeModalBtn}
              onClick={() => setShowImageModal(false)}
            >
              ×
            </button>
            <img 
              src={allImages[selectedImage]} 
              alt={`${getTranslatedContent('name') || product.name} - Full view`}
              className={styles.modalImage}
            />
            {allImages.length > 1 && (
              <div className={styles.modalThumbnails}>
                {allImages.map((image, index) => (
                  <div
                    key={index}
                    className={`${styles.modalThumbnail} ${selectedImage === index ? styles.active : ''}`}
                    onClick={() => setSelectedImage(index)}
                  >
                    <img src={image} alt={`Thumbnail ${index + 1}`} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
} 